import { postContent } from "./post-content";
import { buildHref } from "@softmax/web-addresses";
import type { PostPublic } from "@softmax/observatory-client/types";
import { ApiError } from "@softmax/observatory-client/transport";
import type {
  ForumPostRecord,
  ForumCommentRecord,
} from "@softmax/web-ux/forum";
import type { CommentNode } from "@softmax/web-features/community";
import type { Vote } from "./forum-room.types";

export function presentPost({
  post,
  elevatedPrivileges,
  vote,
}: {
  post: PostPublic;
  elevatedPrivileges: boolean;
  vote: Vote | undefined;
}): ForumPostRecord {
  const content = postContent({ post, elevatedPrivileges });

  return {
    id: post.id,
    title: post.title,
    content,
    score: post.score + (vote?.delta ?? 0),
    createdAt: post.created_at,
    updatedAt: post.edited_at,
    author: {
      ...post.author,
      href:
        post.author.type === "player"
          ? buildHref({
              name: elevatedPrivileges ? "admin.player" : "player",
              playerId: post.author.player_id,
            })
          : undefined,
    },
    images:
      content.format === "deleted"
        ? []
        : (post.media ?? [])
            .toSorted((a, b) => a.ordinal - b.ordinal)
            .map((media, index) => ({
              id: media.media_id,
              src: media.url,
              alt: `${post.title} — image ${index + 1}`,
            })),
    commentCount: post.comment_count,
  };
}
export function presentComments({
  nodes,
  votes,
  viewerUserId,
  elevatedPrivileges,
}: {
  nodes: CommentNode[];
  votes: Record<string, Vote>;
  viewerUserId: string | null | undefined;
  elevatedPrivileges: boolean;
}): ForumCommentRecord[] {
  return nodes.map(({ comment, children }) => ({
    id: comment.id,
    body: comment.body,
    score: comment.score + (votes[comment.id]?.delta ?? 0),
    createdAt: comment.created_at,
    author: {
      ...comment.author,
      href:
        comment.author.type === "player"
          ? buildHref({
              name: elevatedPrivileges ? "admin.player" : "player",
              playerId: comment.author.player_id,
            })
          : undefined,
    },
    canDelete:
      !comment.deleted_at &&
      comment.author.type === "user" &&
      comment.author.user_id === viewerUserId,
    parentId: comment.parent_id ?? null,
    replies: presentComments({
      nodes: children,
      votes,
      viewerUserId,
      elevatedPrivileges,
    }),
  }));
}
export function writeError({
  reason,
  fallback,
}: {
  reason: unknown;
  fallback: string;
}) {
  if (
    reason instanceof ApiError &&
    (reason.status === 401 || reason.status === 403)
  )
    return "Sign in to post.";

  return reason instanceof Error ? reason.message : fallback;
}
